import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  ImportStakeholdersDto,
  StakeholderDto as ApiStakeholderDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";
import { ACTION_GET_STAKEHOLDERS } from "../GetDMStakeholders/useGetDMStakeholders";

import { roleGuard } from "@api";
import { doubleMaterialityStakeholdersApi } from "@api/services";

export const ACTION_IMPORT_STAKEHOLDERS = "IMPORT_STAKEHOLDERS";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

interface ImportStakeholdersResponse {
  data: ApiStakeholderDto[];
}

const importStakeholders = async (
  data: ImportStakeholdersDto,
): Promise<ImportStakeholdersResponse> => {
  const response =
    await doubleMaterialityStakeholdersApi.doubleMaterialityStakeholdersControllerImportStakeholders(
      data,
    );

  return {
    data: response.data,
  };
};

export const useImportDMStakeholders = (
  user: UserProfileDto | null,
): {
  importStakeholders: (data: ImportStakeholdersDto) => void;
  importStakeholdersAsync: (
    data: ImportStakeholdersDto,
  ) => Promise<ImportStakeholdersResponse>;
  isPendingImportStakeholders: boolean;
  isSuccessImportStakeholders: boolean;
  isErrorImportStakeholders: boolean;
  errorImportStakeholders: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const mutation = useMutation({
    mutationFn: importStakeholders,
    onSuccess: (response) => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_STAKEHOLDERS],
      });
      enqueueSnackbar(
        t(
          "stakeholders.messages.importSuccess",
          `Successfully imported ${response.data.length} stakeholders`,
        ),
        {
          variant: "success",
        },
      );
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t("stakeholders.messages.importError"), {
        variant: "error",
      });
    },
  });

  return {
    importStakeholders: mutation.mutate,
    importStakeholdersAsync: mutation.mutateAsync,
    isPendingImportStakeholders: mutation.isPending,
    isSuccessImportStakeholders: mutation.isSuccess,
    isErrorImportStakeholders: mutation.isError,
    errorImportStakeholders: mutation.error,
    enabled,
  };
};
